// eslint-disable-next-line import/no-unresolved
import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, LoaderSvg, LoaderSvgBitiba } from '@zooplus/ui-components';

import { PaymentMethod, ShopBundle } from '../../../types';
import HtmlEmbeddedAlert from '../../Banners/HtmlEmbeddedAlert';
import { AdyenConfig } from '../Adyen.types';

import ActionResultsPortal from './ActionResultsPortal';
import useActionCreator, { ACTION_CREATOR_CONTAINER } from './hooks/useActionCreator';

import './ActionCreator.modules.scss';

export interface ActionCreatorProps extends AdyenConfig {
  paymentAction: PaymentAction;
  locale: string;
  csrfToken?: string;
  shopBundle?: ShopBundle;
  paymentId?: string;
  paymentMethod?: PaymentMethod;
  sendPaymentDetailsHandler?: (payload: string) => void;
  actionTimedOutHandler?: () => void;
  invalidQRCodeActionShopUrl?: string;
}

const ActionCreator: React.FC<ActionCreatorProps> = ({
  paymentAction,
  clientKey,
  adyenEnvironment,
  locale,
  sendPaymentDetailsHandler,
  shopBundle = 'zooplus',
  actionTimedOutHandler,
  invalidQRCodeActionShopUrl,
}) => {
  const { t } = useTranslation();
  const [createPortal, setCreatePortal] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const { detailsPayload, isActionTimedOut } = useActionCreator(paymentAction, {
    clientKey,
    adyenEnvironment,
    locale,
  });

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        mutation.addedNodes.forEach((addedNode) => {
          if ((addedNode as Element).classList.contains('adyen-checkout__qr-loader')) {
            setCreatePortal(true);
            setShowLoader(false);
            observer.disconnect();
          }
        });
      });
    });
    observer.observe(document.querySelector(`#${ACTION_CREATOR_CONTAINER}`) as Element, {
      subtree: false,
      childList: true,
    });
  }, []);

  useEffect(() => {
    if (!!detailsPayload && sendPaymentDetailsHandler) {
      sendPaymentDetailsHandler(detailsPayload);
    }
  }, [detailsPayload, sendPaymentDetailsHandler]);

  useEffect(() => {
    if (isActionTimedOut && actionTimedOutHandler) {
      actionTimedOutHandler();
    }
  }, [isActionTimedOut, actionTimedOutHandler]);

  if (paymentAction.type === 'redirect')
    return <div data-zta={ACTION_CREATOR_CONTAINER} id={ACTION_CREATOR_CONTAINER} />;

  return (
    <>
      <div data-zta={ACTION_CREATOR_CONTAINER} id={ACTION_CREATOR_CONTAINER}>
        {showLoader && (
          <div className="payments-loader">
            {shopBundle === 'zooplus' ? <LoaderSvg /> : <LoaderSvgBitiba />}
          </div>
        )}
      </div>

      {createPortal && !detailsPayload && (
        <ActionResultsPortal>
          <HtmlEmbeddedAlert
            variant="warning"
            innerHtml={t('checkout.payment.authorization.invalid', {
              anchor: `<a href="${invalidQRCodeActionShopUrl}">`,
              endAnchor: '</a>',
            })}
          />
        </ActionResultsPortal>
      )}
      {createPortal && detailsPayload && (
        <ActionResultsPortal>
          <Alert variant="info">{t('checkout.payment.authorization.processing')}</Alert>
        </ActionResultsPortal>
      )}
    </>
  );
};

export default ActionCreator;
